import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useLocalStorage } from 'react-use';
import useIsChartCoverageDemo from '../hooks/useIsChartCoverageDemo';
import * as turf from '@turf/turf';
import UIContext from './UIContext';
import { useDebounce } from "@uidotdev/usehooks";
import { useHistory, useLocation } from 'react-router-dom';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
const baseStyleUrl = import.meta.env.VITE_STYLE_URL;


const MapContext = React.createContext(null);

export default MapContext;

const encodeGetParams = p => 
    Object.entries(p).map(kv => kv.map(encodeURIComponent).join("=")).join("&");
  
export function MapContextProvider({ children }) {
    const params = new URLSearchParams(window.location.search);

    const { queryElevation } = useContext(UIContext);
    const mapRef = useRef(null);
    const rulerControlRef = useRef(null);
    const [raster, setRaster] = useLocalStorage('chart-raster', false);
    const [style, setStyle] = useLocalStorage('chart-style', 'day');
    const [hybrid, setHybrid] = useLocalStorage('chart-hybrid', false);
    const [depthUnits, setDepthUnits] = useLocalStorage('depth-units', 'meters');
    const [measureState, setMeasureState] = useState(null);
    const debouncedMeasureState = useDebounce(measureState, 100);
    const [elevationState, setElevationState] = useState(null);
    const [markers, setMarkers] = useState([]);
    const isChartCoverageDemo = useIsChartCoverageDemo();
    const [styleSettings, setStyleSettings] = useState({
        showEncBoundaries: false || isChartCoverageDemo,
        showTileBoundaries: false,
        useLocal: false,
        showNoData: true,
        depthLimit: 3.0
    });
    const [contextMenuLocation, setContextMenuLocation] = useState(null);

    const history = useHistory();
    const location = useLocation();

    const styleUrl = `${baseStyleUrl}&` + encodeGetParams({
        theme: style,
        showEncBoundaries: isChartCoverageDemo || styleSettings.showEncBoundaries,
        depthLimit: styleSettings.depthLimit,
        depthUnits: depthUnits,
        isChartCoverageDemo: isChartCoverageDemo,
        showNoData: styleSettings.showNoData,
        showPlaceNames: true,
        showOverscale: true,
        featurePreview: true,
        showSatellite: hybrid,
        font: 'Open Sans Regular',
        // hideLayers: "bcncar,bcnisd,bcnlat,bcnsaw,bcnspp,boycar,boyinb,boyisd,boylat,boysaw,boyspp,lights,wrecks,lndmrk"
    });
    
    const getTileCoordsForPosition = (zoom, lat, lon) => {
        const lat_rad = (lat) * (Math.PI / 180.0);
        const n = 1 << zoom;
        const xtile = Math.floor((lon + 180.0) / 360.0 * n);
        const ytile = Math.floor((1.0 - Math.asinh(Math.tan(lat_rad)) / Math.PI) / 2.0 * n);
      
        return {
            z: zoom,
            x: xtile,
            y: ytile
        }
    }

    useEffect(() => {
        if (!debouncedMeasureState || debouncedMeasureState?.coordinates?.length < 2) {
            setElevationState(null);
            return;
        }
        const linestring = turf.lineString(debouncedMeasureState.coordinates.map((coord) => (
            coord
        )));

        const updateElevation = async () => {
            const elevationResult = await queryElevation({
                parameters: {},
                geometry: linestring
            });
            setElevationState(elevationResult);
        };
        updateElevation();
    }, [debouncedMeasureState]);

    return (
        <MapContext.Provider
            value={{
                mapRef,
                style,
                setStyle,
                styleSettings,
                setStyleSettings,
                contextMenuLocation,
                setContextMenuLocation,
                markers,
                setMarkers,
                styleUrl,
                getTileCoordsForPosition,
                depthUnits,
                setDepthUnits,
                raster,
                setRaster,
                measureState,
                setMeasureState,
                rulerControlRef,
                elevationState,
                setElevationState,
                hybrid, setHybrid
            }}
        >
            {children}
        </MapContext.Provider>
    );
}
